import { defineStore } from 'pinia'
import { useNuxtApp } from '#imports'
import { watch, nextTick } from 'vue'
import { calculateWeeklyPayment } from '~/layers/base/utils/finance'
import type {
  FilterOption,
  FilterValue,
  BooleanFilterValue,
  TextFilterValue,
  RangeFilterValue,
  ArrayFilterValue,
  ActiveFilters,
  RangeFilter,
  Vehicle,
  VehicleResponse,
  VehicleAddress,
  VehicleAttribute,
  ValueDisplayPair,
  FilterData,
  FilterCounts,
  FilterUISchema,
  FilterPreset
} from '~/types'
import { ref, computed } from 'vue'

// Add type declaration for the filterSync plugin
declare module '#imports' {
  interface NuxtApp {
    $filterSync?: {
      updateURL: () => void;
      applyFiltersFromURL: (query: any) => boolean;
      markUserInteraction: (filterName: string) => void;
      clearURLParameters: () => void;
      hasInteractedWithAnyFilter: boolean;
    }
  }
}

// Add type declaration for the API
declare module '#imports' {
  interface NuxtApp {
    $api: {
      vehicles: {
        getVehicleById: (id: string) => Promise<Vehicle | null>;
      };
    };
  }
}

/**
 * Make/Model filter information
 */
export interface MakeModelFilters {
  makes: string[]
  models: string[]
  badges: string[]
}

/**
 * Constants
 * --------------------------------------------------------------------------------
 */
const LOCAL_STORAGE_KEYS = {
  FILTERS_TIMESTAMP: 'bolt_filters_timestamp'
}

// Filter expiration time in milliseconds (24 hours)
const FILTER_EXPIRATION_TIME = 24 * 60 * 60 * 1000

// Add fine edit logic for specific filters
const FINE_EDIT_FILTERS = {
  transmission: {
    step: 1,
    min: 0,
    max: 100
  },
  drivetrain: {
    step: 1,
    min: 0,
    max: 100
  },
  fuel: {
    step: 1,
    min: 0,
    max: 100
  },
  seats: {
    step: 1,
    min: 0,
    max: 100
  },
  doors: {
    step: 1,
    min: 0,
    max: 100
  },
  kms: {
    step: 1000,
    min: 0,
    max: 500000
  }
}

const DEFAULT_UI_SCHEMA: FilterUISchema = {
  sections: [
    {
      id: 'favorites',
      title: 'Favorites',
      expanded: false,
      filters: ['favorites']
    },
    {
      id: 'search',
      title: 'Search',
      expanded: false,
      filters: ['search_keywords']
    },
    {
      id: 'condition',
      title: 'Condition',
      expanded: false,
      filters: ['condition']
    },
    {
      id: 'make_model',
      title: 'Make & Model',
      expanded: false,
      filters: ['make', 'model', 'badge']
    },
    {
      id: 'price',
      title: 'Weekly Payment',
      expanded: false,
      filters: ['perweek']
    },
    {
      id: 'body',
      title: 'Body',
      expanded: false,
      filters: ['body']
    },
    {
      id: 'transmission',
      title: 'Transmission',
      expanded: false,
      filters: ['transmission']
    },
    {
      id: 'drivetrain',
      title: 'Drivetrain',
      expanded: false,
      filters: ['drivetrain']
    },
    {
      id: 'fuel',
      title: 'Fuel Type',
      expanded: false,
      filters: ['fuel']
    },
    {
      id: 'seats',
      title: 'Seating Capacity',
      expanded: false,
      filters: ['seats']
    },
    {
      id: 'doors',
      title: 'Doors',
      expanded: false,
      filters: ['doors']
    },
    {
      id: 'kms',
      title: 'Kilometers',
      expanded: false,
      filters: ['kms']
    },
    {
      id: 'colour',
      title: 'Colour',
      expanded: false,
      filters: ['colour']
    }
  ]
}

const DEFAULT_FILTER_PRESETS: FilterPreset[] = [
  {
    id: 'family_cars',
    name: 'Family Cars',
    description: 'Vehicles with 5+ seats, SUVs, and wagons',
    filters: {
      body: ['suv', 'wagon', 'hatch'],
      seats: ['5', '6', '7', '8']
    }
  },
  {
    id: 'fuel_efficient',
    name: 'Fuel Efficient',
    description: 'Economical vehicles with low fuel consumption',
    filters: {
      fuel: ['hybrid', 'electric', 'petrol']
    }
  },
  {
    id: 'off_road',
    name: 'Off-Road Ready',
    description: '4WD vehicles suitable for off-road driving',
    filters: {
      drivetrain: ['4wd', 'awd'],
      body: ['suv', 'ute']
    }
  },
  {
    id: 'city_commuter',
    name: 'City Commuter',
    description: 'Compact vehicles ideal for city driving',
    filters: {
      body: ['hatch', 'sedan'],
      transmission: ['automatic']
    }
  },
  {
    id: 'luxury',
    name: 'Luxury Vehicles',
    description: 'Premium vehicles with high-end features',
    filters: {
      price: { min: 50000, max: 1000000 }
    }
  }
]

// Local type definitions
interface VehicleBase {
  id: string;
  price: number;
  kms?: number;
  perweek?: number | { value: string[] | number[] };
  [key: string]: any;
}

interface RangeFilterBase {
  min: number;
  max: number;
}

/**
 * Helper functions
 * --------------------------------------------------------------------------------
 */

/**
 * Safely parse JSON from localStorage
 */
function safeParseJSON<T>(key: string, defaultValue: T): T {
  if (!process.client || typeof window === 'undefined') {
    return defaultValue
  }

  try {
    const item = localStorage.getItem(key)
    if (!item) return defaultValue
    return JSON.parse(item) as T
  } catch (error) {
    return defaultValue
  }
}

/**
 * Safely save JSON to localStorage
 */
function safeSaveJSON<T>(key: string, value: T): void {
  if (!process.client || typeof window === 'undefined') {
    return
  }

  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
  }
}

/**
 * Check if filters data has expired
 */
function haveFiltersExpired(): boolean {
  if (!process.client || typeof window === 'undefined') {
    return true
  }

  try {
    const timestamp = localStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS_TIMESTAMP)
    if (!timestamp) return true

    const lastUpdate = parseInt(timestamp, 10)
    const now = new Date().getTime()
    const hoursSinceUpdate = (now - lastUpdate) / (1000 * 60 * 60)

    return hoursSinceUpdate > 24 // Expire after 24 hours
  } catch (error) {
    return true
  }
}

/**
 * Update the filters timestamp in localStorage
 */
function updateFiltersTimestamp(): void {
  if (!process.client || typeof window === 'undefined') {
    return
  }

  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS.FILTERS_TIMESTAMP, new Date().getTime().toString())
  } catch (error) {
  }
}

/**
 * Checks if only default filters are active
 */
function hasOnlyDefaultFilters(activeFilters: Record<string, any>, filters: Record<string, FilterData>): boolean {
  for (const [filterName, filterValue] of Object.entries(activeFilters)) {
    // Skip favorites if it's false
    if (filterName === 'favorites' && filterValue === false) {
      continue
    }

    // Check if range filters have default values
    if (filterName === 'price' || filterName === 'kms' || filterName === 'perweek') {
      const filter = filters[filterName]
      if (filter && filter.type === 'slider' && filter.data && typeof filterValue === 'object' && filterValue !== null && 'min' in filterValue && 'max' in filterValue) {
        const defaultMin = filter.data.min
        const defaultMax = filter.data.max

        // If the filter value is not at default, it's an active filter
        if (filterValue.min !== defaultMin || filterValue.max !== defaultMax) {
          return false
        }
      } else if (filter && filter.type === 'slider' && filter.data) {
        // If filter definition exists but active value doesn't match expected range structure, treat as non-default (or handle error)
        // Assuming if the value isn't the expected object, it's non-default for safety.
        return false;
      }
      continue
    }

    // Check if search_keywords is empty
    if (filterName === 'search_keywords' && (!filterValue || filterValue.trim() === '')) {
      continue
    }

    // Check if array filters are empty
    if (Array.isArray(filterValue)) {
      // If the array has values, it's an active filter
      if (filterValue.length > 0) {
        return false
      }
      continue
    }

    // If we get here, there's an active non-default filter
    return false
  }

  // If all filters are default or empty, return true
  return true
}

/**
 * Extracts and groups active filters by their types for more efficient filtering
 */
function groupActiveFiltersByType(activeFilters: Record<string, any>): {
  checkboxFilters: Record<string, string[]>;
  makeModelFilters: MakeModelFilters;
  rangeFilters: Record<string, RangeFilter>;
  searchKeywords: string;
  showFavorites: boolean;
} {
  const checkboxFilters: Record<string, string[]> = {}
  const rangeFilters: Record<string, RangeFilter> = {}
  let searchKeywords = ''
  let showFavorites = false

  // Extract make/model/badge filters
  const makeModelFilters: MakeModelFilters = {
    makes: Array.isArray(activeFilters['make'])
           ? activeFilters['make']
           : (activeFilters['make']?.type === 'array' && Array.isArray(activeFilters['make']?.value))
             ? activeFilters['make'].value
             : [],
    models: Array.isArray(activeFilters['model'])
            ? activeFilters['model']
            : (activeFilters['model']?.type === 'array' && Array.isArray(activeFilters['model']?.value))
              ? activeFilters['model'].value
              : [],
    badges: Array.isArray(activeFilters['badge'])
            ? activeFilters['badge']
            : (activeFilters['badge']?.type === 'array' && Array.isArray(activeFilters['badge']?.value))
              ? activeFilters['badge'].value
              : []
  }

  // Process each filter by its type
  for (const [filterName, filterValue] of Object.entries(activeFilters)) {
    // Skip make/model/badge since they're handled separately
    if (['make', 'model', 'badge'].includes(filterName)) {
      continue
    }

    // Handle favorites filter
    if (filterName === 'favorites' && filterValue === true) {
      showFavorites = true
      continue
    }

    // Handle search keywords filter
    if (filterName === 'search_keywords') {
      // Handle different possible formats of the search_keywords filter
      if (typeof filterValue === 'string') {
        searchKeywords = filterValue.trim();
      } else if (typeof filterValue === 'object' && filterValue !== null) {
        if ('value' in filterValue && typeof filterValue.value === 'string') {
          // Handle format { type: 'text', value: 'search terms' }
          searchKeywords = filterValue.value.trim();
        } else if ('text' in filterValue && typeof filterValue.text === 'string') {
          // Handle another possible format
          searchKeywords = filterValue.text.trim();
        }
      }

      continue;
    }

    // Handle array filters for checkboxes
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      checkboxFilters[filterName] = filterValue
      continue
    }

    // Handle new structure with {type: "array", value: [...]}
    if (filterValue && typeof filterValue === 'object' &&
        filterValue.type === 'array' && Array.isArray(filterValue.value) &&
        filterValue.value.length > 0) {
      checkboxFilters[filterName] = filterValue.value
      continue
    }

    // Handle range filters (price, kms, perweek)
    if (typeof filterValue === 'object' && filterValue !== null &&
        'min' in filterValue && 'max' in filterValue) {
      rangeFilters[filterName] = filterValue as RangeFilter
    }
  }

  return { checkboxFilters, makeModelFilters, rangeFilters, searchKeywords, showFavorites }
}

// Add this fuzzy search utility function before the matchesActiveFilters function
/**
 * Performs fuzzy searching for a search term within text
 * Returns a score based on how well the term matches (higher is better match)
 * A score of 0 means no match, while a positive score indicates a match with varying quality
 */
function fuzzySearch(text: string, term: string): number {
  if (!text || !term) return 0;

  // Exact match gets highest score
  if (text.includes(term)) return 1000;

  // Convert to lowercase for case-insensitive comparison
  const textLower = text.toLowerCase();
  const termLower = term.toLowerCase();

  // Exact match after lowercase conversion
  if (textLower.includes(termLower)) return 900;

  let score = 0;
  let termIndex = 0;
  let consecutiveMatches = 0;
  let lastMatchIndex = -2;

  // Scan through text to find matches for characters in the search term
  for (let i = 0; i < textLower.length && termIndex < termLower.length; i++) {
    if (textLower[i] === termLower[termIndex]) {
      // Reward consecutive characters more
      if (lastMatchIndex === i - 1) {
        consecutiveMatches++;
        score += 10 * consecutiveMatches; // Higher score for consecutive matches
      } else {
        consecutiveMatches = 1;
        score += 5; // Base score for a match
      }

      // Reward matches at word boundaries more
      if (i === 0 || textLower[i - 1] === ' ' || textLower[i - 1] === '-') {
        score += 20; // Higher score for word boundaries
      }

      lastMatchIndex = i;
      termIndex++;
    }
  }

  // If we matched all characters in the term, add a completion bonus
  if (termIndex === termLower.length) {
    score += 50;

    // Add bonus for shorter text (more exact match)
    const proximityRatio = termLower.length / textLower.length;
    score += Math.floor(proximityRatio * 100);
  } else {
    // If we didn't match all characters, it's not a good enough match
    return 0;
  }

  return score;
}

/**
 * Checks if a vehicle matches all active filters
 */
function matchesActiveFilters(
  vehicle: Vehicle,
  showFavorites: boolean,
  favorites: string[],
  makeModelFilters: MakeModelFilters,
  checkboxFilters: Record<string, string[]>,
  rangeFilters: Record<string, RangeFilter>,
  searchKeywords: string
): boolean {
  // Check favorites first
  if (showFavorites && !favorites.includes(vehicle.id)) {
    return false;
  }

  // Handle make/model/badge filtering
  if (makeModelFilters.makes.length > 0 || makeModelFilters.models.length > 0 || makeModelFilters.badges.length > 0) {
    // Check make filter
    if (makeModelFilters.makes.length > 0) {
      const vehicleMakeValue = vehicle.make?.value?.[0]
      if (!vehicleMakeValue || !makeModelFilters.makes.some(make => {
        if (!make) return false
        return vehicleMakeValue.toLowerCase() === String(make).toLowerCase()
      })) return false
    }

    // Check model filter
    if (makeModelFilters.models.length > 0) {
      const vehicleModelValue = vehicle.model?.value?.[0]
      const vehicleMakeValue = vehicle.make?.value?.[0]
      
      if (!vehicleModelValue || !makeModelFilters.models.some(model => {
        if (!model) return false
        
        const filterValue = String(model).toLowerCase()
        const vehicleModel = vehicleModelValue.toLowerCase()
        const vehicleMake = (vehicleMakeValue || '').toLowerCase()
        
        // Direct match (for exact model values like "kia_koleos")
        if (vehicleModel === filterValue) {
          return true
        }
        
        // Handle cases where filter is just the model name without make (like "koleos")
        // and vehicle model is in make_model format (like "kia_koleos")
        const filterParts = filterValue.split('_')
        const vehicleParts = vehicleModel.split('_')
        
        if (filterParts.length === 1 && vehicleParts.length >= 2) {
          // Filter is just model name, vehicle is make_model format
          const vehicleModelPart = vehicleParts.slice(1).join('_')
          if (vehicleModelPart === filterValue) {
            return true
          }
        }
        
        // NEW: Handle filters in make_model format when vehicle model is just the model part
        if (filterParts.length >= 2 && vehicleParts.length === 1) {
          const filterMake = filterParts[0]
          const filterModelPart = filterParts.slice(1).join('_')
          if (vehicleMake === filterMake && vehicleModel === filterModelPart) {
            return true
          }
        }
        
        // Handle cases where filter is make_model format but we want to match against
        // a vehicle that might have a different structure
        if (filterParts.length >= 2 && vehicleParts.length >= 2) {
          const filterMake = filterParts[0]
          const filterModelPart = filterParts.slice(1).join('_')
          const vehicleModelPart = vehicleParts.slice(1).join('_')
          
          // Match if make matches and model part matches
          if (vehicleMake === filterMake && vehicleModelPart === filterModelPart) {
            return true
          }
        }
        
        return false
      })) return false
    }

    // Check badge filter
    if (makeModelFilters.badges.length > 0) {
      const vehicleBadgeValue = vehicle.badge?.value?.[0]
      if (!vehicleBadgeValue || !makeModelFilters.badges.some(badge => {
        if (!badge) return false
        return vehicleBadgeValue.toLowerCase() === String(badge).toLowerCase()
      })) return false
    }
  }

  // Check other checkbox filters
  for (const [filterName, selectedValues] of Object.entries(checkboxFilters)) {
    // Skip empty arrays
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) continue;

    // Get the vehicle's attribute using proper structure
    const vehicleAttribute = vehicle[filterName as keyof Vehicle];

    // Check if the attribute exists and has a 'value' property
    if (!vehicleAttribute || typeof vehicleAttribute !== 'object' || !('value' in vehicleAttribute) || !Array.isArray(vehicleAttribute.value)) {
      return false;
    }

    // Get vehicle values, normalized to lowercase for case-insensitive comparison
    const vehicleValues = vehicleAttribute.value.map((v: any) => String(v).toLowerCase());

    // Special handling for color filters
    if (filterName === 'colour') {
      // Convert selected values to lowercase for comparison
      const normalizedSelectedValues = selectedValues.map((v: string) => String(v).toLowerCase());

      // Check if any selected colour matches any vehicle colour (case insensitive)
      const matches = normalizedSelectedValues.some((selectedValue: string) =>
        vehicleValues.includes(selectedValue)
      );

      if (!matches) return false;
      continue; // Skip the normal handling below
    }

    // Normalize all selected values for comparison
    const normalizedSelectedValues = selectedValues.map(sv => String(sv).toLowerCase());

    // Check if any of the vehicle's values match any selected value
    const matches = normalizedSelectedValues.some(selectedValue =>
      vehicleValues.includes(selectedValue)
    );

    if (!matches) {
      return false;
    }
  }

  // Check range filters
  for (const [filterName, range] of Object.entries(rangeFilters)) {
    if (filterName === 'price') {
      const price = Number(vehicle.price ?? 0);
      if (price < range.min || price > range.max) {
        return false;
      }
    } else if (filterName === 'kms') {
      const kms = Number(vehicle.kms ?? 0);
      if (kms < range.min || kms > range.max) {
        return false;
      }
    } else if (filterName === 'perweek') {
      const currentPrice = Number(vehicle.price ?? 0);
      const perweek = calculateWeeklyPayment(currentPrice);

      // Apply the filter (only if perweek has a valid value > 0)
      // Assuming 0 per week shouldn't be filtered out by a range
      if (perweek > 0 && (perweek < range.min || perweek > range.max)) {
        return false;
      }
    }
  }

  // Check search keywords
  if (searchKeywords) {
    const lowerSearchKeywords = searchKeywords.toLowerCase().trim();

    // Only apply search if there are actual keywords (not empty spaces)
    if (lowerSearchKeywords) {
      // Split search into individual terms for better matching
      const searchTerms = lowerSearchKeywords.split(/\s+/).filter((term: string) => term.length > 0);

      // Check all relevant fields that might contain searchable information
      const fieldsToSearch = [
        vehicle.make?.value?.[0]?.toLowerCase() || '',                  // Make
        vehicle.model?.value?.[0]?.toLowerCase() || '',                 // Model
        vehicle.badge?.value?.[0]?.toLowerCase() || '',                 // Badge/variant
        String(vehicle.year || ''),                                     // Year
        vehicle.stockid || '',                                          // Stock ID
        vehicle.title?.toLowerCase() || '',                             // Title
        vehicle.description?.toLowerCase() || '',                        // Description
        vehicle.body?.value?.[0]?.toLowerCase() || '',                  // Body type
        vehicle.transmission?.value?.[0]?.toLowerCase() || '',          // Transmission
        vehicle.colour?.value?.[0]?.toLowerCase() || '',                // Color
        vehicle.fuel?.value?.[0]?.toLowerCase() || '',                  // Fuel type
        vehicle.drivetrain?.value?.[0]?.toLowerCase() || '',            // Drivetrain
        String(vehicle.price || ''),                                    // Price
        String(vehicle.kms || ''),                                      // Kilometers
        vehicle.vin || '',                                              // VIN
        vehicle.rego || '',                                             // Registration
        vehicle.specification || '',                                    // Specification
        vehicle.series?.value?.[0]?.toLowerCase() || '',                // Series
        // Add search_keywords field if it exists
        vehicle.search_keywords?.toLowerCase() || ''                    // Search keywords
      ];

      // Concat make + model for common search pattern
      const makeModel = `${vehicle.make?.value?.[0]?.toLowerCase() || ''} ${vehicle.model?.value?.[0]?.toLowerCase() || ''}`;
      fieldsToSearch.push(makeModel);

      // Also add displayValues which may contain better search text
      if (vehicle.make?.displayValue?.[0]) fieldsToSearch.push(vehicle.make.displayValue[0].toLowerCase());
      if (vehicle.model?.displayValue?.[0]) fieldsToSearch.push(vehicle.model.displayValue[0].toLowerCase());
      if (vehicle.badge?.displayValue?.[0]) fieldsToSearch.push(vehicle.badge.displayValue[0].toLowerCase());

      // For a single search term, try fuzzy matching against all fields
      if (searchTerms.length === 1) {
        const term = searchTerms[0];
        if (!term) return false;

        // Check for exact matches first (faster)
        const exactMatch = fieldsToSearch.some(field => field.includes(term));
        if (exactMatch) return true;

        // If no exact match, try fuzzy search
        // A vehicle matches if any field scores high enough in fuzzy search
        const MINIMUM_FUZZY_SCORE = 30; // Adjust this threshold based on testing
        return fieldsToSearch.some(field => {
          const score = fuzzySearch(field, term);
          return score >= MINIMUM_FUZZY_SCORE;
        });
      } else {
        // For multiple search terms, handle fuzzy matching for each term
        // This is the key part for multi-word searches like "used ford"
        return searchTerms.every(term => {
          // Check for exact matches first
          const exactMatch = fieldsToSearch.some(field => field.includes(term));
          if (exactMatch) return true;

          // If no exact match, try fuzzy search
          const MINIMUM_FUZZY_SCORE = 30; // Adjust this threshold based on testing
          const fuzzyMatch = fieldsToSearch.some(field => {
            const score = fuzzySearch(field, term);
            return score >= MINIMUM_FUZZY_SCORE;
          });

          // Log for debugging multi-word searches
          if (!exactMatch && !fuzzyMatch) {
            // console.log(`[vehiclesStore] Term '${term}' from search '${lowerSearchKeywords}' did not match any fields`);
          }

          return fuzzyMatch;
        });
      }
    }
  }

  return true
}

/**
 * Vehicles Store
 * --------------------------------------------------------------------------------
 */
const storeId = 'vehicles';

// Define State Type Alias
type VehiclesState = {
  vehicles: Vehicle[];
  filteredVehicles: Vehicle[];
  activeFilters: Record<string, any>;
  availableFilters: {
    make: string[];
    model: string[];
    badge: string[];
    body: string[];
    condition: string[];
    transmission: string[];
    drivetrain: string[];
    fuel: string[];
    seats: string[];
    doors: string[];
    colour: string[];
    priceRange: { min: number; max: number };
    yearRange: { min: number; max: number };
    kmsRange: { min: number; max: number };
    perweekRange?: { min: number; max: number };
  };
  isLoading: boolean;
  isDataLoaded: boolean;
  error: string | null;
  sortOption: string;
  favoriteVehicleIds: string[];
};

export const useVehiclesStore = defineStore(storeId, () => {
  // --- State ---
  const vehicles = ref<Vehicle[]>([]);
  const activeFilters = ref<Record<string, any>>({});
  const isLoading = ref(false);
  const isDataLoaded = ref(false);
  const error = ref<string | null>(null);
  const favoriteVehicleIds = ref<string[]>([]);
  const availableFilters = ref<VehiclesState['availableFilters']>({
      make: [],
      model: [],
      badge: [],
      body: [],
      condition: [],
      transmission: [],
      drivetrain: [],
      fuel: [],
      seats: [],
      doors: [],
      colour: [],
      // Default ranges
      priceRange: { min: 0, max: 100000 },
      yearRange: { min: 1990, max: new Date().getFullYear() },
      kmsRange: { min: 0, max: 500000 },
      // perweekRange can be initialized later if needed
    });
  const sortOption = ref('relevance');
  
  // NEW: Separate AI-selected vehicles state (bypasses normal filter system)
  const aiSelectedVehicles = ref<Vehicle[]>([]);
  const isAIFilterActive = ref(false); // Add sortOption to store state, default if needed

  // --- Filtering Helper ---
  function vehicleMatchesFilters(vehicle: Vehicle, filters: Record<string, any>): boolean {
    // Restore original filtering logic (without special favorite handling)
    return Object.entries(filters).every(([key, value]) => {
       if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
         return true; // Skip empty/null filters
       }

       // Use the filtering logic previously in filteredVehicles computed
       switch (key) {
         case 'make':
         case 'model':
         case 'badge':
         case 'body':
         case 'fuel':
         case 'transmission':
         case 'condition':
         case 'drivetrain':
         case 'seats':
         case 'doors':
         case 'colour':
           { // Added block scope for clarity
             const vehicleProp = vehicle[key as keyof Vehicle] as ValueDisplayPair | undefined;
             const vehicleValue = String(vehicleProp?.value?.[0] || '').toLowerCase();

             let filterValues: string[] = [];
             if (value && typeof value === 'object' && value.type === 'array' && Array.isArray(value.value)) {
               filterValues = value.value;
             } else if (Array.isArray(value)) {
               filterValues = value;
             } else if (value !== null && value !== undefined) {
                filterValues = [String(value)];
             }

             if (filterValues.length === 0) return true; // No specific options selected for this filter

             const filterValueArrayLower = filterValues.map(val => String(val).toLowerCase());

             // Special handling for model filters
             if (key === 'model') {
               const vehicleMakeValue = vehicle.make?.value?.[0]
               const vehicleMake = (vehicleMakeValue || '').toLowerCase()
               
               return filterValueArrayLower.some(filterValue => {
                 // Direct match (for exact model values like "kia_koleos")
                 if (vehicleValue === filterValue) {
                   return true
                 }
                 
                 // Handle cases where filter is just the model name without make (like "koleos")
                 // and vehicle model is in make_model format (like "kia_koleos")
                 const filterParts = filterValue.split('_')
                 const vehicleParts = vehicleValue.split('_')
                 
                 if (filterParts.length === 1 && vehicleParts.length >= 2) {
                   // Filter is just model name, vehicle is make_model format
                   const vehicleModelPart = vehicleParts.slice(1).join('_')
                   if (vehicleModelPart === filterValue) {
                     return true
                   }
                 }

                  // NEW: Handle filters in make_model format when vehicle model is just the model part
                  if (filterParts.length >= 2 && vehicleParts.length === 1) {
                    const filterMake = filterParts[0]
                    const filterModelPart = filterParts.slice(1).join('_')
                    if (vehicleMake === filterMake && vehicleValue === filterModelPart) {
                      return true
                    }
                  }
                 
                 // Handle cases where filter is make_model format
                 if (filterParts.length >= 2 && vehicleParts.length >= 2) {
                   const filterMake = filterParts[0]
                   const filterModelPart = filterParts.slice(1).join('_')
                   const vehicleModelPart = vehicleParts.slice(1).join('_')
                   
                   // Match if make matches and model part matches
                   if (vehicleMake === filterMake && vehicleModelPart === filterModelPart) {
                     return true
                   }
                 }
                 
                 return false
               })
             }

             // Compare directly for non-model array keys
             return filterValueArrayLower.includes(vehicleValue);
           }

         case 'price':
         case 'priceRange':
           const price = Number(vehicle.price ?? 0);
           return typeof value === 'object' && value !== null && 'min' in value && 'max' in value &&
                  price >= value.min && price <= value.max;
         case 'kms':
         case 'kmsRange':
            const kms = Number(vehicle.kms ?? 0);
            return typeof value === 'object' && value !== null && 'min' in value && 'max' in value &&
                   kms >= value.min && kms <= value.max;
         case 'year':
         case 'yearRange':
            let vehicleYearNum: number | undefined;
            if (typeof vehicle.year === 'number') { vehicleYearNum = vehicle.year; }
            else if (isValueDisplayPair(vehicle.year) && vehicle.year.value?.[0]) { vehicleYearNum = Number(vehicle.year.value[0]); }
            return typeof value === 'object' && value !== null && 'min' in value && 'max' in value &&
                   vehicleYearNum !== undefined && !isNaN(vehicleYearNum) &&
                   vehicleYearNum >= value.min && vehicleYearNum <= value.max;
         case 'perweek':
            // Handle perweek filter directly in vehicleMatchesFilters
            const currentPrice = Number(vehicle.price ?? 0);
            const perweek = calculateWeeklyPayment(currentPrice);
            return typeof value === 'object' && value !== null && 'min' in value && 'max' in value &&
                   (perweek === 0 || (perweek >= value.min && perweek <= value.max));

         case 'search_keywords':
            if (typeof value === 'string') {
                const lowerSearch = value.toLowerCase().trim();
                if (!lowerSearch) return true;
                const searchWords = lowerSearch.split(/\s+/);
                const searchableText = [
                    (vehicle.title || '').toLowerCase(),
                    (vehicle.make?.value?.[0] || '').toLowerCase(),
                    (vehicle.model?.value?.[0] || '').toLowerCase(),
                    (vehicle.badge?.value?.[0] || '').toLowerCase(),
                    (vehicle.condition?.value?.[0] || '').toLowerCase(),
                    (vehicle.body?.value?.[0] || '').toLowerCase(),
                    (vehicle.colour?.value?.[0] || '').toLowerCase(),
                    (vehicle.transmission?.value?.[0] || '').toLowerCase(),
                    (vehicle.fuel?.value?.[0] || '').toLowerCase(),
                    (vehicle.drivetrain?.value?.[0] || '').toLowerCase(),
                    (vehicle.stockid || '').toLowerCase(),
                    (vehicle.description || '').toLowerCase(),
                    String(vehicle.year || ''),
                    String(vehicle.price || ''),
                    String(vehicle.kms || '')
                ].join(' ');
                return searchWords.every(word => searchableText.includes(word));
            }
            return true;

         case 'ai_vehicle_ids':
            // Handle AI-selected vehicle IDs - only show vehicles in this list
            if (typeof value === 'string') {
                const allowedIds = value.split(',').map(id => id.trim()).filter(Boolean);
                return allowedIds.includes(vehicle.id);
            }
            return true;

         default:
           return true; // Ignore unknown filters
       }
     });
  }

  // --- Getters (Computed Properties) ---

  // Main filtered list using the helper
  const filteredVehicles = computed(() => {
    if (!isDataLoaded.value) return [];
    
    // PRIORITY CHECK: If AI filter is active, return AI-selected vehicles directly
    if (isAIFilterActive.value && aiSelectedVehicles.value.length > 0) {
      return aiSelectedVehicles.value; // Return AI vehicles directly, no further filtering needed
    }
    
    // 1. Apply activeFilters (normal flow)
    let result = vehicles.value.filter(vehicle => vehicleMatchesFilters(vehicle, activeFilters.value));

    // 2. Apply Sorting
    const vehiclesToSort = [...result]; // Work on a copy
    switch (sortOption.value) {
        case 'relevance':
            // For "Most Relevant", we might not apply any additional sort here,
            // letting the order be as it came from filtering 'vehicles.value'.
            break; 
        case 'price_asc':
            vehiclesToSort.sort((a, b) => (Number(a.price ?? 0)) - (Number(b.price ?? 0)));
            break;
        case 'price_desc':
            vehiclesToSort.sort((a, b) => (Number(b.price ?? 0)) - (Number(a.price ?? 0)));
            break;
        case 'kms_asc':
            vehiclesToSort.sort((a, b) => (Number(a.kms ?? 0)) - (Number(b.kms ?? 0)));
            break;
        case 'kms_desc':
            vehiclesToSort.sort((a, b) => (Number(b.kms ?? 0)) - (Number(a.kms ?? 0)));
            break;
        case 'updated_desc': {
            const getUpdated = (v: Vehicle) => {
              const raw = (v as any).lastUpdated
              const d = raw ? new Date(raw) : null
              return d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : 0
            }
            vehiclesToSort.sort((a, b) => getUpdated(b) - getUpdated(a))
            break;
        }
        case 'year_desc':
            const getYearDesc = (v: Vehicle) => typeof v.year === 'number' ? v.year : Number(v.year?.value?.[0] ?? 0);
            vehiclesToSort.sort((a, b) => getYearDesc(b) - getYearDesc(a));
            break;
        case 'year_asc':
            const getYearAsc = (v: Vehicle) => typeof v.year === 'number' ? v.year : Number(v.year?.value?.[0] ?? 0);
            vehiclesToSort.sort((a, b) => getYearAsc(a) - getYearAsc(b));
            break;
        default:
          // console.log(`[vehiclesStore] Unknown sortOption: ${sortOption.value}, or default sort, not re-sorting.`);
          break; 
    }
    return vehiclesToSort;
  });

  // Faceted counts getter (Replaces previous filterOptionCounts)
  const filterOptionCounts = computed(() => {
    const facetedCounts: Record<string, Record<string, number>> = {};
    // Define keys for which faceted counts are needed
    const keysToCount: (keyof VehiclesState['availableFilters'])[] = [
        'condition', 'make', 'model', 'badge', 'body', 'fuel',
        'transmission', 'drivetrain', 'seats', 'doors', 'colour'
        // Exclude range keys automatically as they are not string[]
    ];

    if (!isDataLoaded.value || vehicles.value.length === 0) {
      return facetedCounts;
    }

    // Initialize structure based on availableFilters keys
    for (const key of keysToCount) {
        if(typeof key === 'string' && Array.isArray(availableFilters.value[key])) {
             facetedCounts[key] = {};
        }
    }

    const currentActiveFilters = { ...activeFilters.value };

    // Calculate counts for each filter key
    for (const targetKey of keysToCount) {
         if (typeof targetKey !== 'string' || !facetedCounts[targetKey]) continue; // Skip non-string keys or those not initialized

      // Create filters excluding the target key AND related keys appropriately
      const tempFilters = { ...currentActiveFilters };
      if (targetKey === 'make') {
        // When counting makes, ignore make, model, and badge filters
        delete tempFilters['make'];
        delete tempFilters['model'];
        delete tempFilters['badge'];
      } else if (targetKey === 'model') {
         // When counting models, ignore model and badge, but KEEP make
         delete tempFilters['model'];
         delete tempFilters['badge'];
      } else if (targetKey === 'badge') {
         // When counting badges, ignore badge, but KEEP make and model
         delete tempFilters['badge'];
      } else {
        // For other filters, just exclude the target key itself
        delete tempFilters[targetKey];
      }

      // Filter full list based on other active filters
      const intermediateFilteredVehicles = vehicles.value.filter(vehicle =>
            vehicleMatchesFilters(vehicle, tempFilters)
        );

        // Count options for the targetKey within the intermediate list
        for (const vehicle of intermediateFilteredVehicles) {
            const prop = vehicle[targetKey as keyof Vehicle]; // Use type assertion
            let value: string | null = null;

            // Extract value logic (same as before)
             if (prop && typeof prop === 'object' && 'value' in prop && Array.isArray(prop.value) && prop.value.length > 0) {
                 value = String(prop.value[0] ?? '').trim();
             } else if (typeof prop === 'string' || typeof prop === 'number') {
                 value = String(prop).trim();
             }

            if (value && value !== '') {
                facetedCounts[targetKey][value] = (facetedCounts[targetKey][value] || 0) + 1;
            }
        }
    } // End loop over targetKey

    return facetedCounts;
  });

  // Computed getters
  const vehicleCount = computed(() => vehicles.value.length)
  const filteredVehicleCount = computed(() => filteredVehicles.value.length)

  // --- Actions (Functions) ---

  function initializeAvailableFilters() {
      if (!vehicles.value || vehicles.value.length === 0) { return; }

      const getUniqueValues = (key: keyof Vehicle): string[] => {
          // Helper to extract value, convert to string, handle potential missing values
          const extractValue = (v: Vehicle): string | null => {
              const prop = v[key];
              if (prop && typeof prop === 'object' && 'value' in prop && Array.isArray(prop.value)) {
                  return String(prop.value[0] ?? '').trim();
              }
              // Handle cases where the property might be a direct string/number (though schema says object)
              if (typeof prop === 'string' || typeof prop === 'number') {
                   return String(prop).trim();
              }
              return null;
          };

          return [
              ...new Set(vehicles.value.map(extractValue).filter(Boolean))
          ].sort() as string[];
      };

      // Calculate Range Filters first
      const validPrices = vehicles.value.map(v => Number(v.price ?? 0)).filter(p => p > 0);
      const priceRange = {
          min: validPrices.length > 0 ? Math.min(...validPrices) : 0,
          max: validPrices.length > 0 ? Math.max(...validPrices) : 100000,
      };

      // Calculate perweek range based on price range
      // Use calculateWeeklyPayment from finance utils
      const minWeekly = calculateWeeklyPayment(priceRange.min);
      const maxWeekly = calculateWeeklyPayment(priceRange.max);
      const perweekRange = {
          min: Math.floor(minWeekly),
          max: Math.ceil(maxWeekly),
      };

      const validYears = vehicles.value
          .map(v => {
              if (typeof v.year === 'number') return v.year;
              // Use helper guard and check value exists
              if (isValueDisplayPair(v.year) && v.year.value?.[0]) return Number(v.year.value[0]);
              return NaN;
          })
          .filter(y => !isNaN(y));
      const yearRange = {
          min: validYears.length > 0 ? Math.min(...validYears) : 1990,
          max: validYears.length > 0 ? Math.max(...validYears) : new Date().getFullYear(),
      };

      const validKms = vehicles.value.map(v => Number(v.kms ?? 0)).filter(k => k > 0);
      const kmsRange = {
          min: validKms.length > 0 ? Math.min(...validKms) : 0,
          max: validKms.length > 0 ? Math.max(...validKms) : 500000,
      };

      // Assign all filters
      availableFilters.value = {
          make: getUniqueValues('make'),
          model: getUniqueValues('model'), // Add model here
          body: getUniqueValues('body'),
          transmission: getUniqueValues('transmission'),
          drivetrain: getUniqueValues('drivetrain'),
          fuel: getUniqueValues('fuel'),
          seats: getUniqueValues('seats'),
          doors: getUniqueValues('doors'),
          colour: getUniqueValues('colour'),
          // Include other keys if they need options
          condition: getUniqueValues('condition'), // Add condition if needed
          badge: getUniqueValues('badge'),       // Add badge if needed

          // Assign calculated ranges
          priceRange: priceRange,
          yearRange: yearRange,
          kmsRange: kmsRange,
          perweekRange: perweekRange, // Add perweekRange
      };
      

    }

  async function fetchVehicles() {
    // --- Guard against redundant fetching ---
    // If data is already loaded (likely from SSR hydration), don't fetch again on client mount.
    if (isDataLoaded.value || vehicles.value.length > 0) {
      // console.log('[vehicles] Action: fetchVehicles SKIPPED (already loaded)'); // Optional: for debugging
      return;
    }
    // --- End Guard ---

    isLoading.value = true;
    isDataLoaded.value = false; // Reset only if we are actually fetching
    error.value = null;
    try {
      // Fetch expecting the VehicleResponse object structure - Use the real-time database endpoint
      const response = await $fetch<VehicleResponse>('/api/vehicles-realtime');

      // Access the vehiclesData property
      const data = response?.vehiclesData;

       const mappedData = (data || []).map(v => ({
           ...v,
           price: Number(v.price ?? 0),
           kms: Number(v.kms ?? 0)
       }));
      vehicles.value = mappedData;
      initializeAvailableFilters();
      isDataLoaded.value = true;
    } catch (err) {
      // console.error(`[vehicles] Action: fetchVehicles FAILED:`, err);
      vehicles.value = [];
      isDataLoaded.value = false;
      error.value = err instanceof Error ? err.message : String(err);
    } finally {
      isLoading.value = false;
    }
  }

  function applyFilters(filters: Record<string, any>) {
    // console.log("[vehiclesStore] applyFilters received:", JSON.stringify(filters)); // DEV LOG
    const parsedFilters: Record<string, any> = {};
    // Define all potential filter types
    const arrayKeys = ['condition', 'make', 'model', 'badge', 'body', 'fuel', 'transmission', 'drivetrain', 'colour', 'seats', 'doors'];
    const rangeKeys = ['perweek', 'price', 'kms', 'year']; // Added 'year' for completeness
    const textKeys = ['search_keywords', 'ai_search_query', 'ai_vehicle_ids'];
    // Add other types like boolean if needed

    // Iterate through the input filters from the URL or other source
    for (const key in filters) {
        if (!Object.prototype.hasOwnProperty.call(filters, key)) continue;

        const value = filters[key];

        // Skip null, undefined, or empty strings
        if (value === null || value === undefined || value === '') continue;

        // Handle Array Keys (potentially comma-separated strings)
        if (arrayKeys.includes(key)) {
            if (typeof value === 'string') {
                const parsedArray = value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(s => s.toLowerCase()); // normalize to canonical slug format
                if (parsedArray.length > 0) {
                    parsedFilters[key] = parsedArray;
                }
            } else if (Array.isArray(value) && value.length > 0) {
                // If already an array, normalize each entry to lowercase slug
                parsedFilters[key] = value
                  .map((s: any) => typeof s === 'string' ? s.trim().toLowerCase() : s)
                  .filter((v: any) => v !== '' && v !== null && v !== undefined);
            }
            // Ignore empty arrays or other types for array keys
        }
        // Handle Range Keys (expecting object {min, max} or separate _min/_max keys)
        else if (rangeKeys.includes(key) && typeof value === 'object' && 'min' in value && 'max' in value) {
            // If range object is passed directly (e.g., from internal calls)
            parsedFilters[key] = value;
        }
        // Handle individual min/max parts (common from URL)
        else if (key.endsWith('_min') || key.endsWith('_max')) {
            const baseKey = key.replace(/_min$|_max$/, '');
            const type = key.endsWith('_min') ? 'min' : 'max';
            if (rangeKeys.includes(baseKey)) {
                 if (!parsedFilters[baseKey]) {
                     // Initialize range object using defaults from availableFilters
                     const defaultRange = availableFilters.value[`${baseKey}Range` as keyof typeof availableFilters.value] || {min: 0, max: 1000000}; // Fallback defaults
                     parsedFilters[baseKey] = { ...defaultRange };
                 }
                 // Ensure value is a number
                 const numValue = Number(value);
                 if (!isNaN(numValue)) {
                     parsedFilters[baseKey][type] = numValue;
                 }
            }
        }
        // Handle Text Keys
        else if (textKeys.includes(key) && typeof value === 'string') {
            const trimmedValue = value.trim();
            if (trimmedValue) {
                parsedFilters[key] = trimmedValue;
            }
        }
        // Handle other potential filter types (e.g., boolean) if needed
        // else if (booleanKeys.includes(key)) { ... }
        else {
            // Fallback for any other keys (might need adjustment based on filter types)
            parsedFilters[key] = value;
        }
    }

    // console.log("[vehiclesStore] Parsed filters for update:", JSON.stringify(parsedFilters));

    // *** START NEW UPDATE LOGIC ***
    // Mutate existing activeFilters object instead of replacing it
    const currentKeys = Object.keys(activeFilters.value);
    const newKeys = Object.keys(parsedFilters);
    let changed = false;

    // Remove keys that are in current but not in new
    currentKeys.forEach(key => {
      if (!newKeys.includes(key)) {
        delete activeFilters.value[key];
        // console.log(`[vehiclesStore applyFilters] MUTATE Removed key: ${key}`);
        changed = true;
      }
    });

    // Add or update keys from new
    newKeys.forEach(key => {
      // Check if value is actually different using JSON stringify for deep comparison
      if (!(key in activeFilters.value) || JSON.stringify(activeFilters.value[key]) !== JSON.stringify(parsedFilters[key])) {
         activeFilters.value[key] = parsedFilters[key];
         // console.log(`[vehiclesStore applyFilters] MUTATE Updated/Added key: ${key} = ${JSON.stringify(parsedFilters[key])}`);
         changed = true;
      }
    });

    if (!changed) {
      // console.log("[vehiclesStore applyFilters] No change detected after parsing.");
    } else {
       // Force reactivity update by assigning a shallow copy
       activeFilters.value = { ...activeFilters.value }; 
       // console.log("[vehiclesStore applyFilters] Forced reactivity trigger.");
    }
    // *** END NEW UPDATE LOGIC ***

    // console.log("[vehiclesStore] activeFilters after applyFilters MUTATE:", JSON.stringify(activeFilters.value)); // DEV LOG
  }

  async function fetchVehicleById(id: string): Promise<Vehicle | null> {
    // Remove NuxtApp dependency for this direct fetch
    // const nuxtApp = useNuxtApp()
    // if (!nuxtApp.$api || !nuxtApp.$api.vehicles) {
    //   console.error('[$api.vehicles] API methods not available.')
    //   return null
    // }

    // Check if vehicle is already in the store
    const existingVehicle = vehicles.value.find(v => v.id === id)
    if (existingVehicle) {
      return existingVehicle
    }

    // If not in store, fetch from API using $fetch
    isLoading.value = true
    error.value = null
    try {
      // Use $fetch to call the specific vehicle endpoint (real-time database)
      const vehicle = await $fetch<Vehicle | null>(`/api/vehicles-realtime/${id}`);

      if (vehicle) {
        // Ensure price and kms are numbers after fetching
        const processedVehicle = {
            ...vehicle,
            price: Number(vehicle.price ?? 0),
            kms: Number(vehicle.kms ?? 0)
        };

        // Add or update the vehicle in the main list if needed
        const index = vehicles.value.findIndex(v => v.id === id)
        if (index === -1) {
          vehicles.value.push(processedVehicle)
        } else {
          vehicles.value[index] = processedVehicle
        }
        return processedVehicle; // Return the processed vehicle
      }
      return null; // Return null if API returned null/undefined
    } catch (err: any) {
      // console.error(`Error fetching vehicle by ID ${id} using $fetch:`, err)
      // Handle potential $fetch errors (like 404)
       if (err.response?.status === 404) {
            error.value = `Vehicle not found with ID: ${id}`;
       } else {
            error.value = `Failed to load vehicle details: ${err.message || err}`
       }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Initializes favorite IDs from localStorage. Client-side only.
   */
  function initFavorites() {
    if (process.client) {
      const storedFavorites = localStorage.getItem('boltv5_favorites');
      if (storedFavorites) {
        try {
          favoriteVehicleIds.value = JSON.parse(storedFavorites);
        } catch (e) {
          console.error('[vehiclesStore] Error parsing favorites from localStorage:', e);
          favoriteVehicleIds.value = []; // Reset if parsing fails
        }
      } else {
        favoriteVehicleIds.value = []; // Initialize as empty if not found
      }
    }
  }

  /**
   * Checks if a vehicle is in the favorites list.
   * @param id - The ID of the vehicle to check.
   * @returns True if the vehicle is a favorite, false otherwise.
   */
  function isFavorite(id: string): boolean {
    return favoriteVehicleIds.value.includes(id);
  }

  /**
   * Toggles the favorite status of a vehicle.
   * @param id - The ID of the vehicle to toggle.
   */
  function toggleFavorite(id: string): void {
    if (!process.client) return;

    const index = favoriteVehicleIds.value.indexOf(id);

    if (index > -1) {
      favoriteVehicleIds.value.splice(index, 1); // Remove from reactive list
    } else {
      favoriteVehicleIds.value.push(id); // Add to reactive list
    }

    localStorage.setItem('boltv5_favorites', JSON.stringify(favoriteVehicleIds.value)); // Persist to localStorage

    // Optionally, re-apply filters if the 'favorites' filter is active
    // This part might still be useful if filtering by favorites is a feature
    const currentActiveFilters = activeFilters.value;
    if (currentActiveFilters && typeof currentActiveFilters === 'object' && 'favorites' in currentActiveFilters && currentActiveFilters.favorites) {
       applyFilters({ ...currentActiveFilters }); // Re-trigger filtering
    }
  }

  /**
   * Set AI-selected vehicles (completely bypasses URL system)
   * @param aiVehicles - Array of vehicles selected by AI
   */
  function setAISelectedVehicles(aiVehicles: Vehicle[]): void {
    // Clear any existing AI filter first
    aiSelectedVehicles.value = []
    isAIFilterActive.value = false
    
    if (aiVehicles && aiVehicles.length > 0) {
      // Store vehicles in separate AI state (no URL involvement)
      aiSelectedVehicles.value = aiVehicles
      isAIFilterActive.value = true
    } else {
      console.warn('⚠️ [Vehicles Store] No AI vehicles provided or empty array')
    }
  }
  
  /**
   * Clear AI filter and return to normal filtering
   */
  function clearAIFilter(): void {
    aiSelectedVehicles.value = []
    isAIFilterActive.value = false
  }

  function setSortOption(newSortOption: string) {
    sortOption.value = newSortOption; // This change will trigger the 'filteredVehicles' computed to re-evaluate
  }

  // --- Return state, getters, and actions ---
  return {
    // State
    vehicles,
    activeFilters,
    isLoading,
    isDataLoaded,
    error,
    availableFilters,
    sortOption, // Expose sortOption state
    favoriteVehicleIds, // Expose reactive favorites
    aiSelectedVehicles, // Expose AI vehicles state
    isAIFilterActive, // Expose AI filter status
    // Getters
    filteredVehicles,
    filterOptionCounts,
    vehicleCount,
    filteredVehicleCount,
    // Actions
    fetchVehicles,
    applyFilters,
    fetchVehicleById,
    initializeAvailableFilters,
    setSortOption, // Expose new action
    isFavorite,
    toggleFavorite,
    initFavorites, // Expose init action
    setAISelectedVehicles, // Expose AI vehicles method
    clearAIFilter, // Expose clear AI filter method
  }
});


// Helper type guard moved outside store definition
function isValueDisplayPair(value: any): value is ValueDisplayPair {
  return value && typeof value === 'object' && Array.isArray(value.value);
}
